import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WelcomeBroadcastCard } from "@/components/WelcomeBroadcastCard";
import { AiCostCard } from "@/components/AiCostCard";
import { QuizFunnelCard, RealUsersCard } from "@/components/AdminInsights";
import { LeadsCard } from "@/components/LeadsCard";
import { TestAccountsCard } from "@/components/TestAccountsCard";
import { MindsetAdminCard } from "@/components/MindsetAdminCard";
import { ShieldCheck, Search, Loader2, X, UserPlus, Download, RotateCcw } from "lucide-react";

const PLAN_STYLE = {
  Tester: "bg-[#FD0F10] text-white",
  Pro: "bg-emerald-600 text-white",
  Referral: "bg-indigo-600 text-white",
  Free: "bg-[#1A1D24] text-gray-400",
};

function PlanBadge({ plan }) {
  return (
    <span className={`text-[10px] font-mono-ui uppercase tracking-wider px-2 py-0.5 rounded ${PLAN_STYLE[plan] || PLAN_STYLE.Free}`}>
      {plan}
    </span>
  );
}

function StatCell({ label, value, accent, testid }) {
  return (
    <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-2.5 text-center" data-testid={testid}>
      <div className={`font-display font-black text-2xl ${accent}`}>{value}</div>
      <div className="text-[10px] font-mono-ui uppercase tracking-wider text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return null;
  }
}

export default function Admin() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testers, setTesters] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [revoking, setRevoking] = useState("");
  const [signups, setSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(true);
  const [granting, setGranting] = useState("");
  const [signupQuery, setSignupQuery] = useState("");
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [refundBusy, setRefundBusy] = useState("");
  const loadRefunds = useCallback(async () => {
    try { const { data } = await api.get("/admin/refund-requests"); setRefunds(data.requests || []); } catch { /* ignore */ }
  }, []);
  const actOnRefund = async (id, action) => {
    setRefundBusy(id);
    try {
      await api.post(`/admin/refund-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Refund issued and subscription canceled" : "Request denied");
      loadRefunds();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setRefundBusy("");
  };

  const errText = (e) => e?.response?.data?.detail || "Something went wrong.";

  const resetSprints = async () => {
    if (!window.confirm("Reset ALL sprint progress for EVERY user? This clears every checkbox. Users can re-mark days themselves. This cannot be undone.")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-sprints");
      toast.success(`Sprint progress reset. ${data.deleted} cleared.`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setResetting(false);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch { /* non-critical */ }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setAnalytics(data);
    } catch { /* non-critical */ }
  }, []);

  const loadTesters = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/admin/testers");
      setTesters(data.testers || []);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadSignups = useCallback(async (q = "") => {
    setSignupsLoading(true);
    try {
      const { data } = await api.get("/admin/signups", { params: { limit: 20, q } });
      setSignups(data.signups || []);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSignupsLoading(false);
    }
  }, []);

  useEffect(() => { loadTesters(); loadStats(); loadAnalytics(); loadRefunds(); }, [loadTesters, loadStats, loadAnalytics, loadRefunds]);

  // Debounced signup search
  useEffect(() => {
    const t = setTimeout(() => loadSignups(signupQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [signupQuery, loadSignups]);

  const lookup = async () => {
    const target = email.trim();
    if (!target) return toast.error("Enter a user email first.");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/tester", { params: { email: target } });
      setResult(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  const setTester = async (targetEmail, enabled) => {
    const { data } = await api.post("/admin/tester", { email: targetEmail, enabled });
    return data;
  };

  const refreshLists = () => { loadTesters(); loadSignups(signupQuery.trim()); loadStats(); };

  const toggle = async (enabled) => {
    if (!result) return;
    setSaving(true);
    try {
      const data = await setTester(result.email, enabled);
      setResult(data);
      toast.success(enabled ? `Tester access ON for ${data.email}` : `Tester access OFF for ${data.email}`);
      refreshLists();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (targetEmail) => {
    setRevoking(targetEmail);
    try {
      await setTester(targetEmail, false);
      toast.success(`Tester access removed for ${targetEmail}`);
      if (result?.email === targetEmail) setResult((r) => (r ? { ...r, is_tester: false, is_premium: false, plan: "Free" } : r));
      refreshLists();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setRevoking("");
    }
  };

  const grant = async (targetEmail) => {
    setGranting(targetEmail);
    try {
      await setTester(targetEmail, true);
      toast.success(`Tester access ON for ${targetEmail}`);
      if (result?.email === targetEmail) setResult((r) => (r ? { ...r, is_tester: true, is_premium: true, plan: "Tester" } : r));
      refreshLists();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setGranting("");
    }
  };

  const exportCsv = () => {
    if (!testers.length) return;
    const cols = ["email", "name", "plan", "granted_at", "granted_by", "last_active"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(",")].concat(
      testers.map((t) => cols.map((c) => esc(t[c])).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `testers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${testers.length} tester${testers.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-6 space-y-6" data-testid="admin-page">
      <div>
        <div className="flex items-center gap-2 text-[#FD0F10]">
          <ShieldCheck size={16} />
          <div className="eyebrow">ADMIN · TESTER ACCESS</div>
        </div>
        <h1 className="font-display text-3xl font-black mt-1">Grant tester Pro</h1>
        <p className="text-sm text-gray-400 mt-2 leading-snug">
          Turn on full Pro access for any account, no Stripe checkout needed. Same access as a paid subscriber.
        </p>
        {stats && (
          <div className="mt-4 grid grid-cols-4 gap-2" data-testid="admin-stats">
            <StatCell label="Pro" value={stats.pro} accent="text-emerald-400" testid="admin-stat-pro" />
            <StatCell label="Tester" value={stats.tester} accent="text-[#FD0F10]" testid="admin-stat-tester" />
            <StatCell label="Referral" value={stats.referral} accent="text-indigo-400" testid="admin-stat-referral" />
            <StatCell label="Free" value={stats.free} accent="text-gray-300" testid="admin-stat-free" />
          </div>
        )}
      </div>

      <div className="card-panel p-5 space-y-4">
        <div>
          <label className="text-xs font-mono-ui uppercase tracking-wider text-gray-400">User email</label>
          <div className="mt-1 flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              className="flex-1 bg-[#0B0C0E] border border-[#2A2E39] rounded px-4 py-2.5 outline-none focus:border-[#FD0F10] font-mono-ui"
              placeholder="user@example.com"
              data-testid="admin-email-input"
            />
            <button
              onClick={lookup}
              disabled={loading}
              className="hero-accent text-white font-bold px-4 rounded flex items-center gap-2 disabled:opacity-60"
              data-testid="admin-lookup-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Find
            </button>
          </div>
        </div>

        {result && (
          <div className="border-t border-[#2A2E39] pt-4 space-y-3" data-testid="admin-result">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold" data-testid="admin-result-email">{result.email}</span>
                  <PlanBadge plan={result.plan} />
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{result.name}</div>
              </div>
              <span
                className={`text-[11px] font-mono-ui uppercase tracking-wider px-2 py-1 rounded ${result.is_tester ? "bg-[#FD0F10] text-white" : "bg-[#1A1D24] text-gray-400"}`}
                data-testid="admin-tester-status"
              >
                {result.is_tester ? "Tester ON" : "Tester OFF"}
              </span>
            </div>
            <div className="text-xs text-gray-400" data-testid="admin-premium-status">
              Pro access: <span className={result.is_premium ? "text-emerald-400" : "text-gray-300"}>{result.is_premium ? "Active" : "Inactive"}</span>
            </div>
            {result.is_tester ? (
              <button
                onClick={() => toggle(false)}
                disabled={saving}
                className="w-full border border-[#2A2E39] hover:border-[#FD0F10] text-white font-bold py-2.5 rounded transition-colors disabled:opacity-60"
                data-testid="admin-toggle-off"
              >
                {saving ? "Saving…" : "Turn tester access OFF"}
              </button>
            ) : (
              <button
                onClick={() => toggle(true)}
                disabled={saving}
                className="w-full hero-accent text-white font-bold py-2.5 rounded disabled:opacity-60"
                data-testid="admin-toggle-on"
              >
                {saving ? "Saving…" : "Turn tester access ON"}
              </button>
            )}
          </div>
        )}
      </div>

      <LeadsCard />
      <TestAccountsCard />
      <RealUsersCard />
      <QuizFunnelCard />
      <AiCostCard />
      <WelcomeBroadcastCard />

      <div className="card-panel p-5" data-testid="admin-refund-requests">
        <div className="eyebrow text-gray-400">REFUND REQUESTS</div>
        {refunds.length === 0 ? (
          <div className="mt-3 text-sm text-gray-500">No refund requests.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {refunds.map(r => (
              <div key={r.id} className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3 text-sm" data-testid={`refund-row-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-white font-bold break-all">{r.email}</span>
                  <span className={`text-[10px] font-mono-ui uppercase px-2 py-0.5 rounded ${r.status === "approved" ? "bg-green-900 text-green-300" : r.status === "denied" || r.status === "failed" ? "bg-[#2A2E39] text-gray-400" : "bg-[#FD0F10] text-white"}`}>{r.status}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">Reason: <span className="text-gray-200">{r.reason}</span></div>
                {r.comment ? <div className="text-xs text-gray-400 mt-0.5">"{r.comment}"</div> : null}
                <div className="text-[11px] text-gray-500 mt-1">Signup {r.signup_date ? new Date(r.signup_date).toLocaleDateString() : "—"} · First paid {r.first_payment_at ? new Date(r.first_payment_at).toLocaleDateString() : "—"} · Requested {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
                {["requested", "processing"].includes(r.status) && (
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => actOnRefund(r.id, "approve")} disabled={refundBusy === r.id} className="hero-accent text-white font-bold px-3 py-1.5 rounded text-xs disabled:opacity-60" data-testid={`refund-approve-${r.id}`}>{refundBusy === r.id ? "…" : "Approve (full refund)"}</button>
                    <button onClick={() => actOnRefund(r.id, "deny")} disabled={refundBusy === r.id} className="border border-[#2A2E39] text-gray-300 px-3 py-1.5 rounded text-xs disabled:opacity-60" data-testid={`refund-deny-${r.id}`}>Deny</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-panel p-5" data-testid="admin-analytics">
        <div className="eyebrow text-gray-400">LANDING PAGE TRAFFIC</div>
        {!analytics ? (
          <div className="mt-4 flex items-center gap-2 text-gray-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCell label="Visits" value={analytics.total_visits} accent="text-white" testid="admin-visits-total" />
              <StatCell label="Unique" value={analytics.unique_visitors} accent="text-white" testid="admin-visits-unique" />
              <StatCell label="Signups" value={analytics.signups} accent="text-emerald-400" testid="admin-visits-signups" />
              <StatCell label="Conv. %" value={`${analytics.conversion_pct}%`} accent="text-[#FD0F10]" testid="admin-visits-conv" />
            </div>
            <div className="mt-4 text-[11px] font-mono-ui uppercase tracking-wider text-gray-500">By source (visits · signups)</div>
            <ul className="mt-2 space-y-1.5" data-testid="admin-visits-by-source">
              {[["tiktok", "TikTok"], ["facebook", "Facebook"], ["direct", "Direct"], ["other", "Other"]].map(([key, label]) => {
                const s = analytics.by_source?.[key] || { visits: 0, signups: 0 };
                return (
                  <li key={key} className="flex items-center justify-between text-sm" data-testid={`admin-source-${key}`}>
                    <span className="text-gray-300">{label}</span>
                    <span className="font-mono-ui text-gray-400">{s.visits} · <span className="text-emerald-400">{s.signups}</span></span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      <div className="card-panel p-5" data-testid="admin-testers-list">
        <div className="flex items-center justify-between">
          <div className="eyebrow text-gray-400">CURRENT TESTERS</div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCsv}
              disabled={!testers.length}
              className="flex items-center gap-1 text-[11px] font-mono-ui uppercase tracking-wider text-gray-400 hover:text-white border border-[#2A2E39] hover:border-[#FD0F10] px-2.5 py-1 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="admin-export-csv"
            >
              <Download size={12} /> Export CSV
            </button>
            <span className="text-xs text-gray-500 font-mono-ui" data-testid="admin-testers-count">{testers.length}</span>
          </div>
        </div>
        {listLoading ? (
          <div className="mt-4 flex items-center gap-2 text-gray-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : testers.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500" data-testid="admin-testers-empty">No tester accounts yet. Grant one above.</p>
        ) : (
          <ul className="mt-3 divide-y divide-[#2A2E39]">
            {testers.map((t) => (
              <li key={t.id} className="flex items-center justify-between py-3" data-testid={`admin-tester-row-${t.email}`}>
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{t.email}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {t.name}
                    {fmtDate(t.granted_at) && <span> · added {fmtDate(t.granted_at)}</span>}
                    <span> · {t.last_active ? `active ${fmtDate(t.last_active)}` : "never active"}</span>
                    <span> · {t.msg_count || 0} msgs</span>
                  </div>
                </div>
                <button
                  onClick={() => revoke(t.email)}
                  disabled={revoking === t.email}
                  className="shrink-0 ml-3 flex items-center gap-1 text-[11px] font-mono-ui uppercase tracking-wider border border-[#2A2E39] hover:border-[#FD0F10] hover:text-white text-gray-400 px-2.5 py-1.5 rounded transition-colors disabled:opacity-60"
                  data-testid={`admin-revoke-${t.email}`}
                >
                  {revoking === t.email ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card-panel p-5" data-testid="admin-signups-list">
        <div className="flex items-center justify-between gap-3">
          <div className="eyebrow text-gray-400">RECENT SIGNUPS</div>
          <div className="relative flex-1 max-w-[220px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={signupQuery}
              onChange={(e) => setSignupQuery(e.target.value)}
              placeholder="Search email…"
              className="w-full bg-[#0B0C0E] border border-[#2A2E39] rounded pl-8 pr-3 py-1.5 text-sm outline-none focus:border-[#FD0F10]"
              data-testid="admin-signup-search"
            />
          </div>
        </div>
        {signupsLoading ? (
          <div className="mt-4 flex items-center gap-2 text-gray-400 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : signups.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500" data-testid="admin-signups-empty">{signupQuery.trim() ? "No accounts match that email." : "No signups yet."}</p>
        ) : (
          <ul className="mt-3 divide-y divide-[#2A2E39]">
            {signups.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-3 gap-3" data-testid={`admin-signup-row-${s.email}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-white text-sm font-semibold truncate">{s.email}</span>
                    <PlanBadge plan={s.plan} />
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {s.name}
                    {fmtDate(s.created_at) && <span> · joined {fmtDate(s.created_at)}</span>}
                    <span> · {s.msg_count || 0} msgs</span>
                  </div>
                </div>
                {s.is_tester ? (
                  <span className="shrink-0 text-[11px] font-mono-ui uppercase tracking-wider text-[#FD0F10]" data-testid={`admin-signup-istester-${s.email}`}>Tester</span>
                ) : (
                  <button
                    onClick={() => grant(s.email)}
                    disabled={granting === s.email}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-mono-ui uppercase tracking-wider hero-accent text-white px-2.5 py-1.5 rounded disabled:opacity-60"
                    data-testid={`admin-grant-${s.email}`}
                  >
                    {granting === s.email ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                    Make tester
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <MindsetAdminCard />

      <div className="card-panel p-5 border-[#FD0F10]/30" data-testid="admin-danger-zone">
        <div className="eyebrow text-[#FD0F10]">DANGER ZONE</div>
        <p className="text-sm text-gray-400 mt-2 leading-snug">
          Reset every user's 7-Day Launch Sprint checkboxes to empty. Days only count again once a user marks them. This cannot be undone.
        </p>
        <button
          onClick={resetSprints}
          disabled={resetting}
          className="mt-3 flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-white border border-[#FD0F10] hover:bg-[#FD0F10] px-3 py-2 rounded transition-colors disabled:opacity-60"
          data-testid="admin-reset-sprints"
        >
          {resetting ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
          Reset all sprint progress
        </button>
      </div>
    </div>
  );
}
